<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { titleCase } from '@/utils/format';

// The learner's observed values for the report card (DepEd core values):
// rate the seven behaviour statements for one quarter, or see every
// quarter's ratings side by side.
const props = defineProps({
  // Row from My Advisory Class: id (the student_list entry, which is what
// student_values.studentId and the report card use), roomId, name,
// grade_level, seniorJunior.
  student: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const toast = useToast();

const CORE_VALUES = [
  {
    name: 'Maka-Diyos',
    statements: [
      { key: 'md1_values', text: "Expresses one's spiritual beliefs while respecting the spiritual beliefs of others." },
      { key: 'md2_values', text: 'Shows adherence to ethical principles by upholding truth.' },
    ],
  },
  {
    name: 'Makatao',
    statements: [
      { key: 'mt1_values', text: 'Is sensitive to individual, social and cultural differences.' },
      { key: 'mt2_values', text: 'Demonstrates contributions towards solidarity.' },
    ],
  },
  {
    name: 'Maka-kalikasan',
    statements: [
      { key: 'mk1_values', text: 'Cares for the environment and utilizes resources wisely, judiciously and economically.' },
    ],
  },
  {
    name: 'Makabansa',
    statements: [
      { key: 'mb1_values', text: 'Demonstrates pride in being a Filipino; exercises the rights and responsibilities of a Filipino citizen.' },
      { key: 'mb2_values', text: 'Demonstrates appropriate behavior in carrying out activities in the school, community and country.' },
    ],
  },
];
const KEYS = CORE_VALUES.flatMap((v) => v.statements.map((s) => s.key));
const MARKINGS = [
  { value: 'AO', label: 'Always observed', variant: 'green' },
  { value: 'SO', label: 'Sometimes observed', variant: 'blue' },
  { value: 'RO', label: 'Rarely observed', variant: 'amber' },
  { value: 'NO', label: 'Not observed', variant: 'red' },
];
const markingOf = (v) => MARKINGS.find((m) => m.value === v);

const TABS = [
  { id: 'rate', name: 'Rate a quarter' },
  { id: 'all', name: 'All quarters' },
];
const tab = shallowRef('rate');

const syType = computed(() => store.getters.getSyType);
const schoolYear = computed(() => store.getters.getFilterSelected);
const isSHS = computed(() => ['Grade 11', 'Grade 12'].includes(props.student?.grade_level));
const quarterOptions = computed(() =>
  isSHS.value
    ? ['1st Quarter', '2nd Quarter']
    : syType.value == 0
      ? ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter']
      : ['1st Term', '2nd Term', '3rd Term'],
);

const quarter = shallowRef(null);
const semester = shallowRef(null);
const ratings = ref({});
const savedId = shallowRef(null);
const loading = shallowRef(false);
const saving = shallowRef(false);

const ratedCount = computed(() => KEYS.filter((k) => ratings.value[k]).length);
const studentName = computed(() => titleCase(props.student?.name));

async function loadQuarter() {
  if (!props.student || !quarter.value) return;
  loading.value = true;
  try {
    const { id, roomId } = props.student;
    const rows = await apiRequest(
      'get',
      `/enroll-student/getValuesData/${schoolYear.value}/${id}/${roomId}/${quarter.value}/${semester.value}`,
    );
    const row = Array.isArray(rows) && rows.length ? rows[0] : null;
    savedId.value = row?.id ?? null;
    ratings.value = Object.fromEntries(KEYS.map((k) => [k, row?.[k] ?? null]));
  } catch (e) {
    toast.error(errorMessage(e, 'Could not load the ratings.'));
  } finally {
    loading.value = false;
  }
}

watch(open, (isOpen) => {
  if (!isOpen || !props.student) return;
  tab.value = 'rate';
  quarter.value = quarterOptions.value[0];
  semester.value = isSHS.value ? '1st Semester' : 'Junior High';
  allLoaded = false;
  loadQuarter();
});
watch([quarter, semester], () => {
  if (open.value) loadQuarter();
});

function rate(key, value) {
  ratings.value = { ...ratings.value, [key]: ratings.value[key] === value ? null : value };
}

async function save() {
  saving.value = true;
  try {
    const values = Object.fromEntries(KEYS.map((k) => [k, ratings.value[k]]));
    if (savedId.value) {
      assertOk(await apiRequest('patch', `/enroll-student/updateStudentValues/${savedId.value}`, values));
    } else {
      const { id, roomId } = props.student;
      assertOk(
        await apiRequest('post', '/enroll-student/studentValues', {
          ...values,
          roomId,
          studentId: id,
          quarter: quarter.value,
          semester: semester.value,
          school_yearId: schoolYear.value,
        }),
      );
    }
    toast.success(`Observed values saved for ${quarter.value}.`);
    allLoaded = false;
    emit('saved');
    await loadQuarter();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the ratings.'));
  } finally {
    saving.value = false;
  }
}

// All quarters ----------------------------------------------------------
// getAllValuesData answers with fixed slots: 0-3 JHS quarters, 4-7 SHS
// semester quarters, 8-10 terms.
const allRows = shallowRef([]);
const allLoading = shallowRef(false);
let allLoaded = false;

const periods = computed(() => {
  if (isSHS.value) {
    return [
      { slot: 4, label: 'Sem 1 · Q1' },
      { slot: 5, label: 'Sem 1 · Q2' },
      { slot: 6, label: 'Sem 2 · Q1' },
      { slot: 7, label: 'Sem 2 · Q2' },
    ];
  }
  return syType.value == 0
    ? [0, 1, 2, 3].map((slot) => ({ slot, label: `Q${slot + 1}` }))
    : [8, 9, 10].map((slot, i) => ({ slot, label: `Term ${i + 1}` }));
});

async function loadAll() {
  allLoading.value = true;
  try {
    const { id, seniorJunior } = props.student;
    const data = await apiRequest(
      'get',
      `/enroll-student/getAllValuesData/${schoolYear.value}/${id}/${syType.value}/${seniorJunior}`,
    );
    allRows.value = Array.isArray(data) ? data : [];
    allLoaded = true;
  } catch (e) {
    toast.error(errorMessage(e, 'Could not load the ratings.'));
  } finally {
    allLoading.value = false;
  }
}
watch(tab, (value) => {
  if (value === 'all' && !allLoaded) loadAll();
});
const ratingAt = (slot, key) => allRows.value[slot]?.[key] || null;
</script>

<template>
  <UiDialog
    v-model="open"
    title="Observed values"
    :description="[studentName, student?.grade_level].filter(Boolean).join(' · ')"
    size="xl"
  >
    <PageTabs v-model="tab" :items="TABS" class="ov-tabs" />

    <!-- Rate one quarter -->
    <template v-if="tab === 'rate'">
      <div class="ov-toolbar">
        <v-select v-model="quarter" :items="quarterOptions" label="Quarter" hide-details class="ov-toolbar__field" />
        <v-select
          v-if="isSHS"
          v-model="semester"
          :items="['1st Semester', '2nd Semester']"
          label="Semester"
          hide-details
          class="ov-toolbar__field"
        />
        <div class="ov-toolbar__status">
          <UiBadge :variant="ratedCount === KEYS.length ? 'green' : 'secondary'">
            {{ ratedCount }} of {{ KEYS.length }} rated
          </UiBadge>
          <span class="ov-muted">{{ savedId ? 'Saved' : 'Not saved yet' }}</span>
        </div>
      </div>

      <div class="ov-legend">
        <span v-for="m in MARKINGS" :key="m.value"><strong>{{ m.value }}</strong> {{ m.label }}</span>
      </div>

      <div class="ov-list" :class="{ 'ov-list--loading': loading }">
        <section v-for="(core, i) in CORE_VALUES" :key="core.name" class="ov-core">
          <h3 class="ov-core__name">{{ i + 1 }}. {{ core.name }}</h3>
          <div v-for="s in core.statements" :key="s.key" class="ov-row">
            <p class="ov-row__text">{{ s.text }}</p>
            <div class="ov-seg" role="radiogroup" :aria-label="s.text">
              <button
                v-for="m in MARKINGS"
                :key="m.value"
                type="button"
                role="radio"
                class="ov-seg__btn"
                :class="[`ov-seg__btn--${m.variant}`, { 'ov-seg__btn--on': ratings[s.key] === m.value }]"
                :aria-checked="ratings[s.key] === m.value"
                :title="m.label"
                @click="rate(s.key, m.value)"
              >
                {{ m.value }}
              </button>
            </div>
          </div>
        </section>
      </div>
    </template>

    <!-- Every quarter side by side -->
    <template v-else>
      <div v-if="allLoading" class="ov-empty">Loading…</div>
      <div v-else class="ov-grid">
        <table>
          <thead>
            <tr>
              <th class="ov-grid__statement">Core value and behavior statement</th>
              <th v-for="p in periods" :key="p.slot" class="ov-grid__period">{{ p.label }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(core, i) in CORE_VALUES" :key="core.name">
              <tr class="ov-grid__core">
                <td :colspan="periods.length + 1">{{ i + 1 }}. {{ core.name }}</td>
              </tr>
              <tr v-for="s in core.statements" :key="s.key">
                <td class="ov-grid__statement">{{ s.text }}</td>
                <td v-for="p in periods" :key="p.slot" class="ov-grid__period">
                  <UiBadge v-if="markingOf(ratingAt(p.slot, s.key))" :variant="markingOf(ratingAt(p.slot, s.key)).variant">
                    {{ ratingAt(p.slot, s.key) }}
                  </UiBadge>
                  <span v-else class="ov-muted">—</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="ov-legend ov-legend--below">
        <span v-for="m in MARKINGS" :key="m.value"><strong>{{ m.value }}</strong> {{ m.label }}</span>
      </div>
    </template>

    <template #footer="{ close }">
      <!-- student_values requires every statement, and so does the report card -->
      <span v-if="tab === 'rate' && ratedCount < KEYS.length" class="ov-footer-note">
        Rate all {{ KEYS.length }} statements to save {{ quarter }} ({{ KEYS.length - ratedCount }} left).
      </span>
      <v-btn variant="outlined" @click="close">Close</v-btn>
      <v-btn
        v-if="tab === 'rate'"
        color="primary"
        :loading="saving"
        :disabled="loading || ratedCount < KEYS.length"
        @click="save"
      >
        {{ savedId ? 'Save changes' : 'Save ratings' }}
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.ov-tabs {
  margin-bottom: 16px;
}
.ov-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.ov-toolbar__field {
  flex: 0 0 200px;
}
.ov-toolbar__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.ov-muted {
  font-size: 13px;
  color: var(--muted-foreground);
}
.ov-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-bottom: 12px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.ov-legend strong {
  color: var(--foreground);
}
.ov-legend--below {
  margin: 12px 0 0;
}
.ov-list {
  border: 1px solid var(--border);
  border-radius: 10px;
  transition: opacity 0.15s ease;
}
.ov-list--loading {
  opacity: 0.5;
  pointer-events: none;
}
.ov-core + .ov-core {
  border-top: 1px solid var(--border);
}
.ov-core__name {
  margin: 0;
  padding: 10px 14px 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.ov-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
}
.ov-row:last-child {
  padding-bottom: 12px;
}
.ov-row__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
}
.ov-seg {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 8px;
  background: var(--accent);
}
.ov-seg__btn {
  min-width: 40px;
  padding: 4px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--secondary-foreground);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ov-seg__btn:hover {
  color: var(--foreground);
}
.ov-seg__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--ring);
}
.ov-seg__btn--on {
  background: var(--card);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
/* Same palette as UiBadge, including its dark-mode colours */
.ov-seg__btn--on.ov-seg__btn--green { color: #047857; }
.ov-seg__btn--on.ov-seg__btn--blue { color: #1d4ed8; }
.ov-seg__btn--on.ov-seg__btn--amber { color: #b45309; }
.ov-seg__btn--on.ov-seg__btn--red { color: #b91c1c; }
html.dark .ov-seg__btn--on.ov-seg__btn--green { color: #6ee7b7; }
html.dark .ov-seg__btn--on.ov-seg__btn--blue { color: #93c5fd; }
html.dark .ov-seg__btn--on.ov-seg__btn--amber { color: #fcd34d; }
html.dark .ov-seg__btn--on.ov-seg__btn--red { color: #fca5a5; }

.ov-grid {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.ov-grid table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ov-grid th,
.ov-grid td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
}
.ov-grid thead th {
  font-weight: 500;
  color: var(--muted-foreground);
  text-align: left;
}
.ov-grid tbody tr:last-child td {
  border-bottom: 0;
}
.ov-grid__statement {
  min-width: 280px;
}
.ov-grid__period {
  width: 96px;
  text-align: center !important;
  white-space: nowrap;
}
.ov-grid__core td {
  padding-top: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
  background: var(--subtle);
}
.ov-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.ov-footer-note {
  align-self: center;
  margin-right: auto;
  font-size: 13px;
  color: var(--muted-foreground);
}
@media (max-width: 600px) {
  .ov-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .ov-toolbar__status {
    margin-left: 0;
  }
}
</style>
